import { z } from "zod";

/**
 * One line under a draft saying where a part of it came from, with its words one click away
 * (docs/2026-09-29-draft-offer-source.md). Shared by the offer's source and a question the draft
 * deferred, so both read alike: a label, a plain fact, and one text button that opens the words in
 * place. Browser-safe: zod only.
 */
export const SavedAnswerReachSchema = z.enum([
  "conversation",
  "lead",
  "campaign",
  "campaign_setup",
  "workspace",
]);
export type SavedAnswerReach = z.infer<typeof SavedAnswerReachSchema>;

/** What the draft card shows without a click: which kind of source, per generated revision. */
export const OfferSourceLineSchema = z.strictObject({
  draftRevision: z.number().int().nonnegative(),
  kind: z.enum(["saved_answer", "campaign_email", "lead_email"]),
  reach: SavedAnswerReachSchema.nullable(),
});
export type OfferSourceLine = z.infer<typeof OfferSourceLineSchema>;

/** The kept words, read when the rep asks for them. */
export const OfferPassageSchema = z.discriminatedUnion("state", [
  z.strictObject({
    state: z.literal("found"),
    text: z.string(),
    /** For an email: when it was sent and its message, so the rep can find it in the thread. */
    at: z.string().nullable(),
    messageId: z.string().nullable(),
    /** For a saved answer: false when it has since been replaced or removed. */
    current: z.boolean(),
  }),
  /** The message is gone from the thread, or its words at those offsets changed. */
  z.strictObject({ state: z.literal("moved") }),
  /** The saved answer's record is not in the context store any more. */
  z.strictObject({ state: z.literal("missing") }),
]);
export type OfferPassage = z.infer<typeof OfferPassageSchema>;

const REACH_WORDS: Record<SavedAnswerReach, string> = {
  conversation: "your saved answer for this conversation",
  lead: "your saved answer for this lead",
  campaign: "your saved answer for this campaign",
  campaign_setup: "this campaign's setup",
  workspace: "your saved answer for the workspace",
};

/** The line's fact, after its label: "from the email this lead was sent". */
export function offerSourceFact(line: OfferSourceLine): string {
  if (line.kind === "campaign_email") return "from this campaign's email";
  if (line.kind === "lead_email") return "from the email this lead was sent";
  return `from ${line.reach ? REACH_WORDS[line.reach] : "a saved answer"}`;
}

/** The disclosure's name: the words of an email are a passage, a saved answer is an answer. */
export const offerSourceToggle = (line: OfferSourceLine, open: boolean): string =>
  `${open ? "Hide" : "Show"} ${line.kind === "saved_answer" ? "answer" : "passage"}`;

/** Said in place of the words when they cannot be shown as they were kept. */
export function offerPassageNote(line: OfferSourceLine, passage: OfferPassage): string | null {
  if (passage.state === "moved") return "That passage is no longer in the stored email.";
  if (passage.state === "missing") return "That saved answer is no longer stored.";
  if (line.kind === "saved_answer" && !passage.current)
    return "This answer has since been replaced or removed. The draft used it as shown.";
  return null;
}

/**
 * The offer line for the draft a reply step shows. A rep's edit starts from a generated revision, so
 * an edited draft keeps the line of the newest generated revision at or below it; a revision with no
 * line of its own shows none, never an older draft's.
 */
export function offerLineFor(
  lines: readonly OfferSourceLine[],
  drafts: readonly { revision: number; origin: string }[],
  draftRevision: number,
): OfferSourceLine | null {
  const generated = drafts
    .filter((draft) => draft.origin === "generated" && draft.revision <= draftRevision)
    .reduce<number | null>((newest, draft) => Math.max(newest ?? -1, draft.revision), null);
  if (generated === null) return null;
  return lines.find((line) => line.draftRevision === generated) ?? null;
}

/**
 * A question the draft pointed to the call instead of stopping for (the defer-and-flag change,
 * w156), which the plan detail carries as `deferredQuestions`. Read defensively: a server without
 * the field shows no line, and a row this build cannot read drops only itself.
 */
const DeferredQuestionSchema = z.object({
  speechActId: z.string().min(1),
  ask: z.string().min(1),
  sentence: z.string(),
  note: z.string().min(1),
});
export type DeferredQuestion = z.infer<typeof DeferredQuestionSchema>;

export function deferredQuestionsOf(detail: unknown): DeferredQuestion[] {
  const parsed = z.object({ deferredQuestions: z.array(z.unknown()) }).safeParse(detail);
  if (!parsed.success) return [];
  return parsed.data.deferredQuestions.flatMap((row) => {
    const question = DeferredQuestionSchema.safeParse(row);
    return question.success ? [question.data] : [];
  });
}

/**
 * A note on one draft revision that asks nothing of the rep: the prose judge's remark about a fact,
 * in one sentence (`docs/2026-10-04-rep-edit-hints.md`), or the saved times the reply could not
 * offer when it went out on the booking page (`docs/2026-10-05-booking-page-over-refused-slots.md`). The plan detail carries them as
 * `draftNotes`. Read defensively, as the deferred questions are: a server without the field shows
 * no line, and a row this build cannot read drops only itself.
 */
const DraftNoteSchema = z.object({
  draftRevision: z.number().int().nonnegative(),
  kind: z.enum(["prose_fact", "refused_slots"]),
  text: z.string().trim().min(1),
});
export type DraftNote = z.infer<typeof DraftNoteSchema>;

export function draftNotesOf(detail: unknown): DraftNote[] {
  const parsed = z.object({ draftNotes: z.array(z.unknown()) }).safeParse(detail);
  if (!parsed.success) return [];
  return parsed.data.draftNotes.flatMap((row) => {
    const note = DraftNoteSchema.safeParse(row);
    return note.success ? [note.data] : [];
  });
}

/** The notes written about one revision. A rep's edit is a new revision, so it carries none. */
export function draftNotesFor(notes: readonly DraftNote[], draftRevision: number): DraftNote[] {
  return notes.filter((note) => note.draftRevision === draftRevision);
}

/**
 * The rep's words for an ask. w156 sends the classifier's ask kinds (`answer_pricing` and so on); the
 * bare names stay for any caller that sends those.
 */
const ASK_LABELS: Record<string, string> = {
  answer_pricing: "Pricing",
  confirm_scope: "Scope",
  answer_technical: "Technical question",
  answer_data_source: "Data source",
  provide_references: "References",
  pricing: "Pricing",
  scope: "Scope",
  technical: "Technical question",
  technical_question: "Technical question",
  data_source: "Data source",
  references: "References",
};

/** The line's label: the ask in the rep's words, "Data source" for `data_source`. */
export function deferredQuestionLabel(ask: string): string {
  const known = ASK_LABELS[ask.toLowerCase()];
  if (known) return known;
  const words = ask.replace(/[_-]+/gu, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
